// Copyright (C) 2021 Bystronic Laser AG

import { ComputedRef, onMounted, Ref, ref, watch } from 'vue';
import { useRoute, useRouter } from '@/composables/router';
import { isEmpty } from '@/utils/misc';

/**
 * Syncs filters with URL query parameters.
 *
 * - Adds/sets a URL query parameter when a filter is set.
 * - Sets filter values from URL query parameters when the component loads.
 *
 * This allows to preserve the filters across page reloads or when navigating
 * the browser history as well as to share links, for example.
 *
 * Returns:
 * - `updateUrlQueryParametersFromFilters`. Must be called when the filters
 *   change so their URL query parameters are updated.
 * - `updateFiltersFromUrlQueryParameters`. Returned only for testing.
 *
 * @param filters Ref to an object with the current filter values.
 * @param defaultFilterValues Object with the default values for `filters`.
 * @param filterValueSerializer Function called for each filter to serialize it
 *   into a URL query parameter value.
 * @param queryParameterDeserializer Function called when a URL query parameter
 *   has been changed so `filter` can be updated.
 * @param areApplying Computed returning if the filters are being applied.
 *   That's to say `filters` doesn't have the default value (`defaultFilterValues`).
 * @param emit `emit` function defined in the component with a `filters-change`
 *   event to be emitted when the filters change.
 */
export function useSyncFiltersWithUrlParams<T>(
  filters: Ref,
  defaultFilterValues: T,
  filterValueSerializer: (value: any) => string | string[],
  queryParameterDeserializer: (key: keyof T, value: any) => any,
  areApplying: ComputedRef<boolean>,
  emit: (...params: any[]) => void,
) {
  const router = useRouter();
  const route = useRoute();
  const isUpdatingUrlQueryParametersFromFilters = ref(false);

  onMounted(() => {
    updateFiltersFromUrlQueryParameters();
  });

  /**
   * Updates the URL's query parameters with the current values of the table
   * filters.
   *
   * Example:
   *   filter = { customerName = ['name1', 'name2'] }
   *   URL query parameters: ?customerName=name1&customerName=name2
   */
  async function updateUrlQueryParametersFromFilters() {
    const queryParameters = Object.entries(filters.value)
      .filter(([_, value]) => !isEmpty(value) && value !== false)
      .map(([key, value]) => ({
        [key]: filterValueSerializer(value),
      }))
      .reduce(
        (resultObject, currentValue) => ({ ...resultObject, ...currentValue }),
        {},
      );

    isUpdatingUrlQueryParametersFromFilters.value = true;
    await router.replace({ query: queryParameters });
    isUpdatingUrlQueryParametersFromFilters.value = false;
  }

  /**
   * Updates the filter values with the values from the URL's query parameters.
   *
   * For each query parameters the value of the matching filter is updated by
   * deserializing the query parameter values. Invalid values are ignored.
   *
   * Example:
   *   URL query parameters: ?status=New&customerName=InvalidStatus
   *   filter = { status = [AlertWorkflowStatus.New] }
   */
  function updateFiltersFromUrlQueryParameters() {
    if (isUpdatingUrlQueryParametersFromFilters.value) {
      // When the filters change, the URL is updated, so no need to update the
      // filters again
      return;
    }

    initializeFilters();

    (Object.entries(route.value.query ?? {}) as [keyof T, any][]).forEach(
      ([key, value]) =>
        (filters.value[key] = queryParameterDeserializer(key, value)),
    );

    if (areApplying.value) {
      // Only emit if the URL contains filters
      emitFilters();
    }
  }

  function initializeFilters() {
    filters.value = structuredClone(defaultFilterValues);
  }

  function emitFilters() {
    emit('filters-change', filters.value, areApplying.value);
  }

  watch(
    () => route.value.query,
    () => {
      updateFiltersFromUrlQueryParameters();
    },
  );

  return {
    updateUrlQueryParametersFromFilters,
    updateFiltersFromUrlQueryParameters, // exported for testing
  };
}
